<script lang="ts">
  /**
   * A page that asks a few questions and starts something: a new game (who,
   * how hard, which mode), a room (which variant, the computer's level, a
   * handicap). The game owns the questions; the shell owns the page, the
   * order and the Start button.
   *
   * Top to bottom:
   *
   * 1. **`sections`**, the questions: the game's own composition of the
   *    shell's parts. Each question is a `SectionLabel variant="picker"`
   *    over its control (`ChoiceTiles`, `RadioCards`, `SegmentedControl`,
   *    a `Group` of `Row`s and `ToggleRow`s); the labels carry the spacing,
   *    so nothing else is needed between them.
   * 2. **`summary`** (optional): read-only facts about the configuration
   *    chosen ("Your personal best", "Global record"), as `Row`s with a
   *    `value`. The template puts them in a `Group` under `summaryLabel`.
   * 3. **The footer**, which stays in view: `note` (a centred line, "You get
   *    the code on the next screen"), `error` (announced at once), and
   *    **Start** (`start`: its label names the choice, "Start Normal").
   *    It follows the questions while they fit and sticks to the bottom once
   *    they scroll, so Start is never below the fold on a phone.
   *
   * `frame` (`pageTypes.ts`): `page` (the default) is a `FullPage` that owns
   * its Back, in the `done` dress unless `dress` says otherwise; `screen`
   * is the game's own routed screen under a back chevron, content 36rem wide
   * from 1024px.
   *
   * Test ids: `testid` on the dialog or screen; `testids.done` /
   * `testids.back` on the way out (FullPage's `ui-page-done` /
   * `ui-page-back`, or ScreenHeader's `ui-screen-back`); `start.testid` on
   * Start (default `ui-picker-start`); `testids.error` (default
   * `ui-picker-error`), `testids.note` and `testids.summary` (no defaults).
   */
  import type { Snippet } from 'svelte';

  import Group from '../components/Group.svelte';
  import Note from '../components/Note.svelte';
  import PillButton from '../components/PillButton.svelte';
  import SectionLabel from '../components/SectionLabel.svelte';
  import PageFrame from './PageFrame.svelte';
  import type { PageDress, PageFrame as Frame } from './pageTypes.js';
  import type { TemplateAction } from '../types.js';

  interface Props {
    /** The page's title and accessible name ("New game", "Start a duel"). */
    readonly title: string;
    /** Every way out: Done or Back, Escape and the Back gesture (`page`), the chevron (`screen`). */
    readonly onclose: () => void;
    /** The questions. */
    readonly sections: Snippet;
    /** The button the page is for. */
    readonly start: TemplateAction;
    /** Read-only rows about the configuration chosen. */
    readonly summary?: Snippet | undefined;
    /** The heading over `summary` ("This configuration"). */
    readonly summaryLabel?: string | undefined;
    /** A centred line above Start. */
    readonly note?: string | undefined;
    /** Why Start did not work, as a sentence. */
    readonly error?: string | null | undefined;
    readonly open?: boolean;
    readonly frame?: Frame;
    readonly dress?: PageDress;
    /** The way out's name; the shell's "Done" / "Back" by default. */
    readonly leaveLabel?: string | undefined;
    readonly wide?: boolean;
    readonly testid?: string | undefined;
    readonly testids?:
      | {
          readonly done?: string;
          readonly back?: string;
          readonly error?: string;
          readonly note?: string;
          readonly summary?: string;
        }
      | undefined;
  }
  const {
    title,
    onclose,
    sections,
    start,
    summary,
    summaryLabel,
    note,
    error,
    open = true,
    frame = 'page',
    dress,
    leaveLabel,
    wide = false,
    testid,
    testids,
  }: Props = $props();

  const summaryLabelId = $props.id();
</script>

{#snippet footer()}
  {#if note !== undefined}
    <Note centered weight="bold" testid={testids?.note}>{note}</Note>
  {/if}
  {#if error !== undefined && error !== null}
    <Note tone="error" testid={testids?.error ?? 'ui-picker-error'}>{error}</Note>
  {/if}
  <PillButton
    label={start.label}
    size="lg"
    disabled={start.disabled === true}
    busy={start.busy === true}
    icon={start.icon}
    testid={start.testid ?? 'ui-picker-start'}
    onclick={start.onclick}
  />
{/snippet}

<PageFrame
  {frame}
  {open}
  {title}
  {onclose}
  dress={dress ?? (frame === 'page' ? 'done' : 'chevron')}
  {wide}
  {leaveLabel}
  measure="medium"
  {footer}
  {testid}
  {testids}
>
  <div class="ui-picker-sections">{@render sections()}</div>
  {#if summary !== undefined}
    <div class="ui-picker-summary" data-testid={testids?.summary}>
      {#if summaryLabel !== undefined}
        <SectionLabel variant="picker" id={summaryLabelId}>{summaryLabel}</SectionLabel>
      {/if}
      <Group name={summaryLabel}>{@render summary()}</Group>
    </div>
  {/if}
</PageFrame>

<style>
  .ui-picker-sections {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  /* The summary's group closes the page's content: no gap of its own below. */
  .ui-picker-summary :global(.ui-group) {
    margin-bottom: 0;
  }
</style>
